import React, { useEffect, useRef, useState } from 'react';
import '../../styles.css';
import { Tooltip, Typography } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import IosShareIcon from '@mui/icons-material/IosShare';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import StarIcon from '@mui/icons-material/Star';
import { KratosTokens, LoadoutStats, PowerDieColor } from '../../core/types';
import { Utils } from '../../core/utilities';
import { Constants } from '../../core/constants';
import NumericField from '../../components/NumericField';
import Button from '../../components/Button';

interface LoadoutStatsSectionProps {
    loadouts: LoadoutStats[];
    defaultLoadoutStats: LoadoutStats;
    visibleTags: string[];
    setLoadouts: React.Dispatch<React.SetStateAction<LoadoutStats[]>>;
    advancedMode: boolean;
    onShareLoadout?: (loadout: LoadoutStats, index: number) => Promise<boolean>;
    onToggleFavoriteLoadout?: (loadout: LoadoutStats, index: number) => void;
    isFavoriteLoadout?: (loadout: LoadoutStats, index: number) => boolean;
}

const filterObject = (obj: any): any => {
    if (Array.isArray(obj)) {
        return obj
            .map((item) => {
                if (Array.isArray(item)) {
                    // Filter out invalid PowerDieColor values within each array
                    return item.filter((die) => Object.values(PowerDieColor).includes(die as PowerDieColor));
                }
                return filterObject(item);
            })
            .filter((item) => item !== null && item !== undefined && (Array.isArray(item) ? item.length !== 0 : Object.keys(item).length !== 0));
    } else if (typeof obj === 'object' && obj !== null) {
        const filteredObj = Object.entries(obj).reduce((acc, [key, value]) => {
            const filteredValue = filterObject(value);
            if (
                filteredValue !== null &&
                filteredValue !== 0 &&
                filteredValue !== '' &&
                !(Array.isArray(filteredValue) && filteredValue.length === 0) &&
                !(typeof filteredValue === 'object' && Object.keys(filteredValue).length === 0)
            ) {
                acc[key] = filteredValue;
            }
            return acc;
        }, {} as any);

        return Object.keys(filteredObj).length > 0 ? filteredObj : undefined;
    }

    return obj;
};

// Function to format loadout information for the tooltip (filtered with nested objects)
const formatLoadoutInfo = (loadout: LoadoutStats): string => {
    const filteredLoadout = filterObject(loadout);
    return 'LOADOUT DETAILS\n' + JSON.stringify(filteredLoadout, null, 2); // Format with 2 spaces for indentation
};

const LoadoutStatsSection: React.FC<LoadoutStatsSectionProps> = ({
    loadouts,
    defaultLoadoutStats,
    visibleTags,
    setLoadouts,
    advancedMode,
    onShareLoadout,
    onToggleFavoriteLoadout,
    isFavoriteLoadout,
}) => {
    const [copiedLoadout, setCopiedLoadout] = useState<LoadoutStats | null>(null);
    const shareResetTimeoutRef = useRef<number | null>(null);

    useEffect(() => {
        return () => {
            if (shareResetTimeoutRef.current !== null) {
                window.clearTimeout(shareResetTimeoutRef.current);
            }
        };
    }, []);

    const handleLoadoutChange = (index: number, field: keyof LoadoutStats, value: any) => {
        const updatedLoadouts = [...loadouts];
        if (!updatedLoadouts[index]) return;

        if (['attackReRolls', 'powerReRolls', 'autoHits'].includes(field)) {
            value = Math.max(0, value);
        } else if (['attackDice', 'maxHits'].includes(field)) {
            value = Math.max(value, 1);
        }

        if (field === 'maxHits' || field === 'autoHits') {
            value = Math.min(value, updatedLoadouts[index].attackDice);
        } else if (field === 'attackDice') {
            updatedLoadouts[index].maxHits = value;
            updatedLoadouts[index].autoHits = Math.min(updatedLoadouts[index].autoHits, value);
        }

        const maxHits = field === 'maxHits' ? value : updatedLoadouts[index].maxHits;
        if (updatedLoadouts[index].perHitPowerDiceOverride.length < maxHits) {
            updatedLoadouts[index].perHitPowerDiceOverride = [...updatedLoadouts[index].perHitPowerDiceOverride, ...Array(maxHits - updatedLoadouts[index].perHitPowerDiceOverride.length).fill([])];
        } else if (updatedLoadouts[index].perHitPowerDiceOverride.length > maxHits) {
            updatedLoadouts[index].perHitPowerDiceOverride = updatedLoadouts[index].perHitPowerDiceOverride.slice(0, maxHits);
        }

        if (field === 'overridePowerDice') {
            if (value) {
                updatedLoadouts[index].perHitPowerDice = [];
            } else {
                updatedLoadouts[index].perHitPowerDiceOverride = Array(maxHits).fill([]);
            }
        }

        updatedLoadouts[index] = {
            ...updatedLoadouts[index],
            [field]: value,
        };
        updatedLoadouts[index] = Utils.sanitizeLoadoutStats(updatedLoadouts[index]);
        setLoadouts(updatedLoadouts);
    };

    const handleDuplicateLoadout = (index: number) => {
        const updatedLoadouts = [...loadouts];
        if (!updatedLoadouts[index]) return;

        const duplicatedLoadout = Utils.sanitizeLoadoutStats(Utils.deepCopy(updatedLoadouts[index]));
        updatedLoadouts.push(duplicatedLoadout);

        setLoadouts(updatedLoadouts);
    };

    const handleAddLoadout = () => {
        const updatedLoadouts = [...loadouts];

        const duplicatedLoadout = Utils.sanitizeLoadoutStats(Utils.deepCopy(defaultLoadoutStats));
        updatedLoadouts.push(duplicatedLoadout);

        setLoadouts(updatedLoadouts);
    };

    const handleRemoveLoadout = (index: number) => {
        const updatedLoadouts = [...loadouts];
        updatedLoadouts.splice(index, 1);
        setLoadouts(updatedLoadouts);
    };

    const handleIncrement = (index: number, field: keyof LoadoutStats | keyof KratosTokens, section: 'loadoutStats' | 'autoTokens', increment: number) => {
        if (section === 'loadoutStats') {
            field = field as keyof LoadoutStats;
            handleLoadoutChange(index, field, ((loadouts[index][field] as number) ?? 0) + increment);
        } else if (section === 'autoTokens') {
            field = field as keyof KratosTokens;
            handleAutoTokenChange(index, field, (loadouts[index].autoTokens[field] ?? 0) + increment);
        }
    };

    const handleAutoTokenChange = (index: number, field: keyof KratosTokens, value: number) => {
        const updatedKratosTokens = loadouts[index].autoTokens;
        updatedKratosTokens[field] = Math.max(value, 0);
        handleLoadoutChange(index, 'autoTokens', updatedKratosTokens);
    };

    const handleAddPowerDie = (index: number, field: keyof LoadoutStats, color: string, diceIndex: number = 0) => {
        if (field === 'perHitPowerDiceOverride') {
            const updatedOverride = loadouts[index][field] as PowerDieColor[][];
            const updatedDice = updatedOverride[diceIndex];
            updatedDice.push(color as PowerDieColor);
            handleLoadoutChange(index, field, updatedOverride);
        } else {
            const updatedDice = loadouts[index][field] as PowerDieColor[];
            updatedDice.push(color as PowerDieColor);
            handleLoadoutChange(index, field, updatedDice);
        }
    };

    const handleRemovePowerDie = (index: number, field: keyof LoadoutStats, subindex: number = 0) => {
        if (field === 'perHitPowerDiceOverride') {
            const updatedOverride = loadouts[index][field] as PowerDieColor[][];
            const updatedDice = updatedOverride[subindex];
            updatedDice.pop();
            handleLoadoutChange(index, field, updatedOverride);
        } else {
            const updatedDice = loadouts[index][field] as PowerDieColor[];
            updatedDice.pop();
            handleLoadoutChange(index, field, updatedDice);
        }
    };

    const handleShareClick = async (index: number) => {
        if (!onShareLoadout || !loadouts[index]) {
            return;
        }

        let copied = false;
        try {
            copied = await onShareLoadout(loadouts[index], index);
        } catch {
            copied = false;
        }

        if (!copied) {
            return;
        }

        if (shareResetTimeoutRef.current !== null) {
            window.clearTimeout(shareResetTimeoutRef.current);
        }

        setCopiedLoadout(loadouts[index]);
        shareResetTimeoutRef.current = window.setTimeout(() => {
            setCopiedLoadout(null);
        }, 1800);
    };

    return (
        <div className="simulation-input-section">
            {loadouts.length > 0 ? (
                <div className="loadout-section-container">
                    {loadouts.map((loadout, index) => (
                        <div className="loadout-section" key={index}>
                            <div className="loadout-title-row">
                                <input
                                    type="text"
                                    value={loadout.name}
                                    onChange={(e) => handleLoadoutChange(index, 'name', e.target.value)}
                                    onFocus={(e) => e.currentTarget.select()}
                                    placeholder={`Loadout ${index + 1}`}
                                    className="loadout-title-input"
                                    aria-label={`Loadout ${index + 1} name`}
                                />
                                <Tooltip title={<pre className="tooltip-pre">{formatLoadoutInfo(loadout)}</pre>} arrow enterDelay={500} enterNextDelay={500}>
                                    <Typography component="span" className="loadout-title-info">
                                        <InfoIcon className="info-icon" />
                                    </Typography>
                                </Tooltip>
                            </div>

                        <div className="loadout-row">
                            <NumericField
                                label="Attack Dice"
                                value={loadout.attackDice}
                                onDecrement={() => handleIncrement(index, 'attackDice', 'loadoutStats', -1)}
                                onIncrement={() => handleIncrement(index, 'attackDice', 'loadoutStats', 1)}
                            />
                        </div>

                        {advancedMode && (
                            <>
                                <div className="loadout-row">
                                    <NumericField
                                        label="Max Hits"
                                        value={loadout.maxHits}
                                        onDecrement={() => handleIncrement(index, 'maxHits', 'loadoutStats', -1)}
                                        onIncrement={() => handleIncrement(index, 'maxHits', 'loadoutStats', 1)}
                                    />
                                    <NumericField
                                        label="Auto Hits"
                                        value={loadout.autoHits}
                                        onDecrement={() => handleIncrement(index, 'autoHits', 'loadoutStats', -1)}
                                        onIncrement={() => handleIncrement(index, 'autoHits', 'loadoutStats', 1)}
                                    />
                                </div>
                            </>
                        )}

                        <div className="loadout-row">
                            <NumericField
                                label="Precision Bonus"
                                value={loadout.precisionBonus}
                                onDecrement={() => handleIncrement(index, 'precisionBonus', 'loadoutStats', -1)}
                                onIncrement={() => handleIncrement(index, 'precisionBonus', 'loadoutStats', 1)}
                            />
                            <NumericField
                                label="Attack Re-Rolls"
                                value={loadout.attackReRolls}
                                onDecrement={() => handleIncrement(index, 'attackReRolls', 'loadoutStats', -1)}
                                onIncrement={() => handleIncrement(index, 'attackReRolls', 'loadoutStats', 1)}
                            />
                        </div>

                        <div className="loadout-row">
                            <NumericField
                                label="Power Bonus"
                                value={loadout.powerBonus}
                                onDecrement={() => handleIncrement(index, 'powerBonus', 'loadoutStats', -1)}
                                onIncrement={() => handleIncrement(index, 'powerBonus', 'loadoutStats', 1)}
                            />
                            <NumericField
                                label="Power Re-Rolls"
                                value={loadout.powerReRolls}
                                onDecrement={() => handleIncrement(index, 'powerReRolls', 'loadoutStats', -1)}
                                onIncrement={() => handleIncrement(index, 'powerReRolls', 'loadoutStats', 1)}
                            />
                        </div>

                        {advancedMode && (
                            <div className="loadout-row">
                                <div className="loadout-input-group">
                                    <label className="input-label">
                                        Override Per-Hit Power Dice?
                                        <div className="checkbox-container">
                                            <input type="checkbox" checked={loadout.overridePowerDice} onChange={(e) => handleLoadoutChange(index, 'overridePowerDice', e.target.checked)} />
                                        </div>
                                    </label>
                                </div>
                            </div>
                        )}

                        {advancedMode && loadout.overridePowerDice ? (
                            <div className="loadout-row">
                                <label>Power Dice:</label>
                            </div>
                        ) : (
                            <div className="loadout-row">
                                <label>Per-Hit Power Dice:</label>
                            </div>
                        )}

                        {advancedMode && loadout.overridePowerDice ? (
                            <div className="loadout-row">
                                <div className="loadout-input-group">
                                    <div className="dice-display">
                                        {(loadout.perHitPowerDiceOverride[0] ?? []).map((die, dieIndex) => (
                                            <span key={dieIndex} className={`die ${die.toLowerCase()}-die`} />
                                        ))}
                                    </div>
                                </div>
                                <div className="increment-buttons">
                                    <div className="dice-buttons">
                                        <button type="button" className="dice-button decrement" onClick={() => handleRemovePowerDie(index, 'perHitPowerDiceOverride', 0)}>
                                            −
                                        </button>
                                        <button type="button" className="dice-button" onClick={() => handleAddPowerDie(index, 'perHitPowerDiceOverride', 'Red', 0)}>
                                            <span className="die red-die" />
                                        </button>
                                        <button type="button" className="dice-button" onClick={() => handleAddPowerDie(index, 'perHitPowerDiceOverride', 'Black', 0)}>
                                            <span className="die black-die" />
                                        </button>
                                        <button type="button" className="dice-button" onClick={() => handleAddPowerDie(index, 'perHitPowerDiceOverride', 'White', 0)}>
                                            <span className="die white-die" />
                                        </button>
                                        <button type="button" className="dice-button" onClick={() => handleAddPowerDie(index, 'perHitPowerDiceOverride', 'Mortal', 0)}>
                                            <span className="die mortal-die" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="loadout-row">
                                <div className="loadout-input-group">
                                    <div className="dice-display">
                                        {loadout.perHitPowerDice.map((die, dieIndex) => (
                                            <span key={dieIndex} className={`die ${die.toLowerCase()}-die`}>
                                                {'  '}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                                <div className="increment-buttons">
                                    <div className="dice-buttons">
                                        <button type="button" className="dice-button decrement" onClick={() => handleRemovePowerDie(index, 'perHitPowerDice')}>
                                            −
                                        </button>
                                        <button type="button" className="dice-button" onClick={() => handleAddPowerDie(index, 'perHitPowerDice', 'Red')}>
                                            <span className="die red-die" />
                                        </button>
                                        <button type="button" className="dice-button" onClick={() => handleAddPowerDie(index, 'perHitPowerDice', 'Black')}>
                                            <span className="die black-die" />
                                        </button>
                                        <button type="button" className="dice-button" onClick={() => handleAddPowerDie(index, 'perHitPowerDice', 'White')}>
                                            <span className="die white-die" />
                                        </button>
                                        <button type="button" className="dice-button" onClick={() => handleAddPowerDie(index, 'perHitPowerDice', 'Mortal')}>
                                            <span className="die mortal-die" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {advancedMode && loadout.overridePowerDice ? (
                            <>
                                {loadout.perHitPowerDiceOverride.map(
                                    (powerDiceForHit, diceIndex) =>
                                        diceIndex > 0 && ( // Skip the first index
                                            <div key={`loadout-row-${diceIndex}`} className="loadout-row">
                                                <div className="loadout-input-group">
                                                    <div className="dice-display">
                                                        {powerDiceForHit.map((die, dieIndex) => (
                                                            <span key={`die-${diceIndex}-${dieIndex}`} className={`die ${die.toLowerCase()}-die`}>
                                                                {'  '}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                                <div className="increment-buttons">
                                                    <div className="dice-buttons">
                                                        <button type="button" className="dice-button decrement" onClick={() => handleRemovePowerDie(index, 'perHitPowerDiceOverride', diceIndex)}>
                                                            −
                                                        </button>
                                                        <button type="button" className="dice-button" onClick={() => handleAddPowerDie(index, 'perHitPowerDiceOverride', 'Red', diceIndex)}>
                                                            <span className="die red-die"></span>
                                                        </button>
                                                        <button type="button" className="dice-button" onClick={() => handleAddPowerDie(index, 'perHitPowerDiceOverride', 'Black', diceIndex)}>
                                                            <span className="die black-die"></span>
                                                        </button>
                                                        <button type="button" className="dice-button" onClick={() => handleAddPowerDie(index, 'perHitPowerDiceOverride', 'White', diceIndex)}>
                                                            <span className="die white-die"></span>
                                                        </button>
                                                        <button type="button" className="dice-button" onClick={() => handleAddPowerDie(index, 'perHitPowerDiceOverride', 'Mortal', diceIndex)}>
                                                            <span className="die mortal-die"></span>
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ),
                                )}
                            </>
                        ) : (
                            <></>
                        )}

                        <div className="loadout-row">
                            <label>Additional Power Dice:</label>
                        </div>
                        <div className="loadout-row">
                            <div className="loadout-input-group">
                                <div className="dice-display">
                                    {loadout.powerDice.map((die, dieIndex) => (
                                        <span key={dieIndex} className={`die ${die.toLowerCase()}-die`}></span>
                                    ))}
                                </div>
                            </div>
                            <div className="increment-buttons">
                                <div className="dice-buttons">
                                    <button type="button" className="dice-button decrement" onClick={() => handleRemovePowerDie(index, 'powerDice')}>
                                        −
                                    </button>
                                    <button type="button" className="dice-button" onClick={() => handleAddPowerDie(index, 'powerDice', 'Red')}>
                                        <span className="die red-die" />
                                    </button>
                                    <button type="button" className="dice-button" onClick={() => handleAddPowerDie(index, 'powerDice', 'Black')}>
                                        <span className="die black-die" />
                                    </button>
                                    <button type="button" className="dice-button" onClick={() => handleAddPowerDie(index, 'powerDice', 'White')}>
                                        <span className="die white-die" />
                                    </button>
                                    <button type="button" className="dice-button" onClick={() => handleAddPowerDie(index, 'powerDice', 'Mortal')}>
                                        <span className="die mortal-die" />
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="loadout-row">
                            <NumericField
                                label="Auto-Break"
                                value={loadout.autoTokens.break}
                                onDecrement={() => handleIncrement(index, 'break', 'autoTokens', -1)}
                                onIncrement={() => handleIncrement(index, 'break', 'autoTokens', 1)}
                                tokenKey="break"
                            />
                            {visibleTags.includes(Constants.SpoilerTag.CYCLE_II) && (
                                <NumericField
                                    label="Auto-Black"
                                    value={loadout.autoTokens.black}
                                    onDecrement={() => handleIncrement(index, 'black', 'autoTokens', -1)}
                                    onIncrement={() => handleIncrement(index, 'black', 'autoTokens', 1)}
                                    tokenKey="black"
                                />
                            )}
                        </div>

                        <div className="loadout-row">
                            {visibleTags.includes(Constants.SpoilerTag.CYCLE_II) && (
                                <NumericField
                                    label="Auto-Fire"
                                    value={loadout.autoTokens.fire}
                                    onDecrement={() => handleIncrement(index, 'fire', 'autoTokens', -1)}
                                    onIncrement={() => handleIncrement(index, 'fire', 'autoTokens', 1)}
                                    tokenKey="fire"
                                />
                            )}
                            {visibleTags.includes(Constants.SpoilerTag.CYCLE_III) && (
                                <NumericField
                                    label="Auto-Hope"
                                    value={loadout.autoTokens.hope}
                                    onDecrement={() => handleIncrement(index, 'hope', 'autoTokens', -1)}
                                    onIncrement={() => handleIncrement(index, 'hope', 'autoTokens', 1)}
                                    tokenKey="hope"
                                />
                            )}
                        </div>

                        <div className="spacer" />
                        <div className="input-section-button-container">
                            <Button className="compact-loadout-action-button" variant="secondary" fullWidth onClick={() => onToggleFavoriteLoadout?.(loadouts[index], index)}>
                                {isFavoriteLoadout?.(loadouts[index], index) ? <StarIcon className="loadout-action-icon" /> : <StarBorderIcon className="loadout-action-icon" />}
                                {isFavoriteLoadout?.(loadouts[index], index) ? 'Unfavorite' : 'Favorite'}
                            </Button>
                            <Button className="compact-loadout-action-button" variant="secondary" fullWidth onClick={() => handleShareClick(index)}>
                                <IosShareIcon className="loadout-action-icon" />
                                {copiedLoadout === loadouts[index] ? 'Copied Link' : 'Share'}
                            </Button>
                            <Button className="compact-loadout-action-button" variant="secondary" fullWidth onClick={() => handleDuplicateLoadout(index)}>
                                Duplicate
                            </Button>
                            <Button className="compact-loadout-action-button" variant="danger" fullWidth onClick={() => handleRemoveLoadout(index)}>
                                Remove
                            </Button>
                        </div>
                    </div>
                ))}
            </div>
            ) : (
                <div className="loadout-empty-state">
                    <p className="loadout-empty-message">No manual loadouts yet.</p>
                </div>
            )}

            <div className={`add-loadout-container${loadouts.length === 0 ? ' add-loadout-container-empty' : ''}`}>
                <Button className="add-loadout-button" variant="secondary" size="lg" onClick={handleAddLoadout}>
                    Add Loadout
                </Button>
            </div>
        </div>
    );
};

export default LoadoutStatsSection;
